/**
 * The platform reprice (Feature 68): replay the pricing pass over stored
 * usage events with today's rates, exchange rates, and rules. A period or
 * all time, every capability or some, one pass per organization per month.
 * The scope reads in words beside the button and again in the confirm, and
 * the result is one row per pass so a failed month is visible.
 */
import { listCapabilities } from '@optum/api-client/endpoints/capabilities';
import { reprice } from '@optum/api-client/endpoints/cost';
import type { RepriceBody, RepricePass, RepriceResult } from '@optum/api-client/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../ui/Button.js';
import { useConfirm } from '../../ui/Confirm.js';
import { DataTable } from '../../ui/DataTable.js';
import { SectionHeader } from '../../ui/SectionHeader.js';
import { Toggle } from '../../ui/Toggle.js';
import { Badge, FilterChip } from '../../ui/finixt.js';
import { useToast } from '../../ui/useToast.js';
import { quantity, shortId } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';

import { monthOf, monthRange } from './period.js';

/** The capability name that stands for ingestion-job events, which carry none (D6). */
export const INGESTION = 'ingestion';

/** Every query family a reprice rewrites, so the cost pages refetch after one. */
const COST_FAMILIES = new Set([
  'cost-rollups',
  'cost-events',
  'cost-summary',
  'rate-card',
  'reconciliation',
  'task',
]);

const MONTH_INPUT = {
  borderRadius: 'var(--radius-control)',
  border: '1px solid var(--border-hairline)',
  background: 'var(--surface-card)',
  paddingLeft: 7,
  paddingRight: 7,
  paddingTop: 5,
  paddingBottom: 5,
  fontSize: 'var(--text-body-md)',
  color: 'var(--text-strong)',
} as const;

export interface RepriceDraft {
  allTime: boolean;
  /** "YYYY-MM", inclusive on both ends; the body's end is the month after `to`. */
  from: string;
  to: string;
  capabilities: string[];
}

/** "Every organization · all time · every capability", or the months and names chosen. */
export function scopeSentence(draft: RepriceDraft): string {
  const period = draft.allTime
    ? 'all time'
    : draft.from === draft.to
      ? draft.from
      : `${draft.from} to ${draft.to}`;
  const capabilities =
    draft.capabilities.length === 0 ? 'every capability' : draft.capabilities.join(', ');
  return `Every organization · ${period} · ${capabilities}`;
}

/** The request the draft stands for; null dates mean all time, a null list every capability. */
export function bodyOf(draft: RepriceDraft): RepriceBody {
  return {
    start: draft.allTime ? null : monthRange(draft.from).start,
    end: draft.allTime ? null : monthRange(draft.to).end,
    capability_ids: draft.capabilities.length === 0 ? null : draft.capabilities,
  };
}

function message(error: unknown): string {
  return error instanceof Error ? error.message : 'Failed';
}

export function RepriceCard() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const capabilities = useQuery({ queryKey: qk.capabilities(), queryFn: listCapabilities });
  const [draft, setDraft] = useState<RepriceDraft>(() => {
    const month = monthOf(new Date());
    return { allTime: true, from: month, to: month, capabilities: [] };
  });
  const [result, setResult] = useState<RepriceResult | null>(null);

  const run = useMutation({
    mutationFn: () => reprice(bodyOf(draft)),
    onSuccess: (data) => {
      setResult(data);
      void qc.invalidateQueries({
        predicate: (query) => COST_FAMILIES.has(String(query.queryKey[0])),
      });
      if (data.failed > 0) toast.error(`${data.failed} of ${data.passes.length} passes failed`);
      else toast.success(`Repriced ${quantity(data.events)} events`);
    },
    onError: (error) => toast.error(message(error)),
  });

  const scope = scopeSentence(draft);
  const invalid = !draft.allTime && draft.to < draft.from;
  const options = [...(capabilities.data ?? []).map((c) => c.id), INGESTION];

  function toggleCapability(id: string) {
    setDraft((d) => ({
      ...d,
      capabilities: d.capabilities.includes(id)
        ? d.capabilities.filter((c) => c !== id)
        : [...d.capabilities, id],
    }));
  }

  function ask() {
    void confirm({
      title: 'Reprice every organization?',
      description: `${scope}. Every priced line and day rollup in that scope is rewritten with today's rate card, exchange rates, and rules. Usage events and receipts are not changed, and another reprice can rewrite the result again.`,
      confirmLabel: 'Reprice',
      tone: 'warn',
    }).then((ok) => {
      if (ok) run.mutate();
    });
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SectionHeader
        title="Reprice"
        subtitle="Replay the pricing pass over stored usage events with today's rates, exchange rates, and rules. Lines and rollups are rebuilt; the events are untouched. One pass per organization per month."
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 16,
          borderRadius: 'var(--radius-input)',
          border: '1px solid var(--border-hairline)',
          padding: 16,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
            Period
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <input
              type="month"
              aria-label="From month"
              value={draft.from}
              disabled={draft.allTime}
              onChange={(e) => e.target.value && setDraft({ ...draft, from: e.target.value })}
              style={MONTH_INPUT}
            />
            <span style={{ color: 'var(--text-muted)' }}>to</span>
            <input
              type="month"
              aria-label="To month"
              value={draft.to}
              disabled={draft.allTime}
              onChange={(e) => e.target.value && setDraft({ ...draft, to: e.target.value })}
              style={MONTH_INPUT}
            />
          </div>
          <Toggle
            checked={draft.allTime}
            onChange={(allTime) => setDraft({ ...draft, allTime })}
            label="All time"
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
            Capabilities
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {options.map((id) => (
              <FilterChip
                key={id}
                label={id}
                active={draft.capabilities.includes(id)}
                onClick={() => toggleCapability(id)}
              />
            ))}
          </div>
          <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
            None selected means every capability; ingestion stands for ingestion jobs.
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, gridColumn: '1 / -1' }}>
          <Button variant="primary" onClick={ask} disabled={run.isPending || invalid}>
            Reprice…
          </Button>
          <span style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
            {invalid ? 'The end month is before the start month.' : scope}
          </span>
        </div>
      </div>
      {result && <ResultTable result={result} />}
    </section>
  );
}

function ResultTable({ result }: { result: RepriceResult }) {
  const n = result.passes.length;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <p style={{ margin: 0, fontSize: 'var(--text-body-md)' }}>
        <strong>
          {n} pass{n === 1 ? '' : 'es'}
        </strong>{' '}
        · {quantity(result.events)} events ·{' '}
        <span
          style={{
            color: result.failed > 0 ? 'var(--danger)' : 'var(--text-muted)',
            fontWeight: result.failed > 0 ? 'var(--weight-semibold)' : undefined,
          }}
        >
          {result.failed} failed
        </span>{' '}
        · {result.start} to {result.end}
      </p>
      <DataTable
        rows={result.passes}
        rowKey={(p) => `${p.organization_id}-${p.month}`}
        columns={[
          { key: 'name', label: 'Organization', strong: true },
          { key: 'month', label: 'Month', mono: true, render: (p) => p.month.slice(0, 7) },
          {
            key: 'events',
            label: 'Events',
            align: 'right',
            mono: true,
            render: (p) => quantity(p.events),
          },
          {
            key: 'run',
            label: 'Run',
            mono: true,
            render: (p) => (p.pricing_run_id ? shortId(p.pricing_run_id) : '—'),
          },
          { key: 'status', label: 'Status', render: (p) => <PassStatus pass={p} /> },
        ]}
      />
    </div>
  );
}

function PassStatus({ pass }: { pass: RepricePass }) {
  if (pass.error) {
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <Badge tone="danger">failed</Badge>
        <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--danger)' }}>
          {pass.error}
        </span>
      </span>
    );
  }
  if (pass.events === 0) return <Badge tone="neutral">no events</Badge>;
  return <Badge tone="ok">done</Badge>;
}
